import { useEffect, useRef } from 'react';
import { Background, BackgroundVariant, MiniMap, ReactFlow, useReactFlow } from '@xyflow/react';
import { DataEdge } from './DataEdge';
import { StageLane, SystemNode } from './PfdNodes';
import { buildEdges, buildNodes, getConnected } from '../lib/graph';
import { scenarioById } from '../data/scenarios';
import { nodeById } from '../data/graph';
import { useDiagramStore } from '../state/store';

const nodeTypes = { stageLane: StageLane, systemNode: SystemNode };
const edgeTypes = { dataEdge: DataEdge };

export interface FocusRequest { id: string; key: number }

export function PfdCanvas({ reducedMotion, focusRequest }: { reducedMotion: boolean; focusRequest: FocusRequest | null }) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const flow = useReactFlow();
  const state = useDiagramStore();
  const scenario = scenarioById.get(state.scenarioId)!;
  const currentStep = scenario.steps[state.stepIndex];
  const activeNodeIds = state.status === 'idle' ? new Set<string>() : new Set(currentStep.activeNodeIds);
  const activeEdgeIds = state.status === 'idle' ? new Set<string>() : new Set(currentStep.activeEdgeIds);
  const selectedConnections = getConnected(state.selectedNodeId);
  const hoveredConnections = getConnected(state.hoveredNodeId);
  const connectedNodes = new Set([...selectedConnections.nodes, ...hoveredConnections.nodes]);
  const emphasizedEdges = new Set([...selectedConnections.edges, ...hoveredConnections.edges]);

  const nodes = buildNodes(state.language, activeNodeIds, state.selectedNodeId, connectedNodes, state.hoveredNodeId, state.roleLens, state.focusPath, state.funnelFilter);
  const edges = buildEdges(state.language, activeEdgeIds, emphasizedEdges, Boolean(state.selectedNodeId || state.hoveredNodeId || state.focusPath), state.status === 'playing', reducedMotion, state.funnelFilter);

  useEffect(() => {
    if (!focusRequest) return;
    const target = flow.getNode(focusRequest.id);
    if (target) void flow.fitView({ nodes: [target], padding: 1.2, maxZoom: 1.15, duration: reducedMotion ? 0 : 550 });
  }, [focusRequest, flow, reducedMotion]);

  useEffect(() => {
    const svg = wrapperRef.current?.querySelector('.react-flow__edges') as SVGSVGElement | null;
    if (!svg) return;
    if (state.status === 'paused') svg.pauseAnimations?.();
    if (state.status === 'playing') svg.unpauseAnimations?.();
  }, [state.status, currentStep.id]);

  useEffect(() => {
    if (state.status !== 'playing' || !currentStep.cameraTarget) return;
    const target = flow.getInternalNode(currentStep.cameraTarget.nodeId);
    if (!target) return;
    const rect = wrapperRef.current?.getBoundingClientRect();
    if (!rect || target.measured?.width === undefined || target.measured.height === undefined) return;
    const viewport = flow.getViewport();
    const absoluteX = target.internals.positionAbsolute.x + target.measured.width / 2;
    const absoluteY = target.internals.positionAbsolute.y + target.measured.height / 2;
    const screenX = absoluteX * viewport.zoom + viewport.x;
    const screenY = absoluteY * viewport.zoom + viewport.y;
    if (screenX < 120 || screenX > rect.width - 120 || screenY < 90 || screenY > rect.height - 110) {
      void flow.setCenter(absoluteX, absoluteY, { zoom: currentStep.cameraTarget.zoom ?? viewport.zoom, duration: reducedMotion ? 0 : 650 });
    }
  }, [currentStep.id, currentStep.cameraTarget, state.status, flow, reducedMotion]);

  return (
    <div className="canvas-shell" ref={wrapperRef} data-testid="pfd-canvas">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        fitView
        fitViewOptions={{ padding: 0.055, minZoom: 0.2, maxZoom: 0.78 }}
        minZoom={0.16}
        maxZoom={1.6}
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable
        elevateNodesOnSelect={false}
        panOnScroll
        selectionOnDrag={false}
        onNodeClick={(_, node) => { if (nodeById.has(node.id)) state.selectNode(node.id); }}
        onNodeMouseEnter={(_, node) => { if (nodeById.has(node.id)) state.hoverNode(node.id); }}
        onNodeMouseLeave={() => state.hoverNode(null)}
        onPaneClick={() => state.selectNode(null)}
        aria-label={state.language === 'en' ? 'Interactive customer data process flow diagram' : 'İnteraktiv müştəri datası proses axını diaqramı'}
      >
        <Background variant={BackgroundVariant.Dots} gap={24} size={1} color="#c6c9c5" />
        <MiniMap pannable zoomable className="pfd-minimap" maskColor="rgba(242,240,234,.68)" nodeStrokeWidth={2} />
      </ReactFlow>
    </div>
  );
}
